import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentUser, signOut } from "aws-amplify/auth";
import { OnboardingAPI, OnboardingApiError } from "../services/onboarding.service";
import { ONBOARDING_STATUS_ROUTES } from "../constants";
import { readAuthCache, writeAuthCache, AUTH_USER_ID_KEY } from "../components/RequireAuth";

interface Options {
  onError?: (message: string) => void;
  onDeviceVerificationRequired?: () => void;
}

/**
 * Checks on mount whether the user is already authenticated and redirects
 * them through the getMe → getStatus → navigate chain if so.
 *
 * Returns redirectAfterLogin for callers that also need to trigger the
 * redirect imperatively (e.g. LoginPage's Hub listener and form submit).
 *
 * Matches LoginPage's exact logic:
 * - finally on the outer (second) try so the ref guard spans both API calls
 * - checkAlreadySignedIn wraps redirectAfterLogin in try/catch + signOut()
 *   so a stale Cognito token doesn't leave the page stuck
 */
export function useRedirectIfAuthenticated({ onError, onDeviceVerificationRequired }: Options = {}): {
  redirectAfterLogin: () => Promise<void>;
} {
  const navigate = useNavigate();
  const redirectingRef = useRef(false);

  const redirectAfterLogin = useCallback(async () => {
    if (redirectingRef.current) return;
    redirectingRef.current = true;
    let role = "";
    try {
      ({ role } = await OnboardingAPI.getMe());
      if (!role) {
        navigate("/auth/role", { replace: true });
        return;
      }
    } catch (err) {
      const status = err instanceof OnboardingApiError ? err.statusCode : 0;
      const message = err instanceof OnboardingApiError ? err.message : "";

      if (status === 403 && message === "device_verification_required") {
        // Must reset the guard so the caller can invoke redirectAfterLogin()
        // again after the device is verified.
        redirectingRef.current = false;
        if (onDeviceVerificationRequired) {
          onDeviceVerificationRequired();
        } else {
          navigate("/dashboard", { replace: true });
        }
        return;
      }
      if (status === 404 || status === 401 || status === 403) {
        navigate("/auth/role", { replace: true });
      } else {
        onError?.("Unable to reach the server. Please check your connection and try again.");
      }
      return;
    }

    try {
      const { onboarding_status, clinic_id } = await OnboardingAPI.getStatus();
      // A non-empty role does not mean Admin was ever actually assigned: the
      // device-check screen's first call (backend get_jwt_user, auto_create=True)
      // silently seeds a brand-new signup's row with role="Doctor" the moment it
      // mounts, before the user ever reaches role selection. Once that seeded
      // role comes back non-empty here, this used to skip straight to
      // onboarding-status routing and never send the user back through
      // /auth/role, so RoleProvisioningPage's "self-signup is always Admin"
      // logic never got a chance to run. Route back through /auth/role unless
      // the role is genuinely Admin/PlatformAdmin, or the clinic already exists
      // (role is frozen server-side once clinic_id is set, so it's real then).
      if (role !== "Admin" && role !== "PlatformAdmin" && !clinic_id) {
        navigate("/auth/role", { replace: true });
        return;
      }

      try {
        const user = await getCurrentUser().catch(() => null);
        if (user?.userId) {
          writeAuthCache(user.userId, role, onboarding_status);
        }
      } catch { /* non-fatal */ }

      if (onboarding_status === "completed") {
        navigate("/dashboard", { replace: true });
      } else {
        const route =
          ONBOARDING_STATUS_ROUTES[onboarding_status] ?? "/onboarding/clinic-details";
        navigate(route, { replace: true });
      }
    } catch (err) {
      const status = err instanceof OnboardingApiError ? err.statusCode : 0;
      if (status === 404) {
        navigate("/onboarding/clinic-details", { replace: true });
      } else {
        onError?.("Unable to load your session. Please try again.");
      }
    } finally {
      redirectingRef.current = false;
    }
  }, [navigate, onError, onDeviceVerificationRequired]);

  useEffect(() => {
    let cancelled = false;
    const checkAlreadySignedIn = async () => {
      // Fast path: check cached session before hitting slow network calls
      const storedUserId = localStorage.getItem(AUTH_USER_ID_KEY);
      const cached = storedUserId ? readAuthCache(storedUserId) : null;
      if (cached?.onboarding_status === "completed") {
        navigate("/dashboard", { replace: true });
        return;
      } else if (cached?.onboarding_status) {
        const route =
          ONBOARDING_STATUS_ROUTES[cached.onboarding_status] ?? "/onboarding/clinic-details";
        navigate(route, { replace: true });
        return;
      }

      try {
        await getCurrentUser();
        if (!cancelled) {
          try {
            await redirectAfterLogin();
          } catch {
            // Backend unreachable or session invalid — clear the stale Cognito
            // session so the page is usable instead of stuck in a broken state.
            await signOut().catch(() => {});
          }
        }
      } catch {
        // Not signed in — stay on this page.
      }
    };
    checkAlreadySignedIn();
    return () => {
      cancelled = true;
    };
  }, [navigate, redirectAfterLogin]);

  return { redirectAfterLogin };
}
